import React, { useEffect, useRef } from 'react';
import { SuButton } from '@amzn/seller-university-ui-components';
import useMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';

export interface CredentialCompletionPanelProps {
  /** Earned-state headline shown above the certificate download action. */
  message: string;
  /** Visible label for the certificate download button. */
  downloadLabel: string;
  /** URL of the certificate PDF; used as both the link target and the download filename. */
  certificateUrl: string;
  /** Credential id used to tag the certificate view/download metrics. */
  credentialId: string;
  /** Optional config-driven CTA target (e.g. Pricing Health). Rendered only alongside ctaLabel. */
  ctaLink?: string;
  /** Optional resolved label for the config-driven CTA button. Rendered only alongside ctaLink. */
  ctaLabel?: string;
}

/**
 * True only for http(s) or relative URLs. Guards a link against a non-http(s) scheme
 * (e.g. javascript:/data:) reaching the anchor href; an unsafe or unparseable value hides the
 * button rather than rendering an executable link. Relative paths resolve against the current
 * origin and are therefore treated as safe. Used for both the certificate download link and the
 * config-driven CTA.
 */
const isSafeUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url, window.location.origin);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
};

/**
 * Earned-state panel shown on the credential page once the credential is awarded:
 * a headline plus a primary button that downloads the certificate. The download button is
 * omitted when the certificate URL fails the http(s)/relative scheme check. A config-driven CTA
 * link (e.g. Pricing Health) is rendered alongside the download button only when its target and
 * label are present and the target passes the same http(s)/relative scheme check.
 */
export const CredentialCompletionPanel: React.FC<CredentialCompletionPanelProps> = ({
  message,
  downloadLabel,
  certificateUrl,
  credentialId,
  ctaLink,
  ctaLabel,
}) => {
  const { viewMetric, clickMetric } = useMetrics();

  // Emit the view only when the certificate URL passes the scheme check (button shown), once per credentialId.
  const lastEmittedView = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (lastEmittedView.current === credentialId) return;
    if (!isSafeUrl(certificateUrl)) return;
    lastEmittedView.current = credentialId;
    viewMetric({
      feature: FeatureName.CredentialCertificateViewed,
      metadata: { credentialId },
    });
  }, [viewMetric, credentialId, certificateUrl]);

  return (
    <div className="mx-auto mt-su-space-05 flex flex-col items-center gap-su-space-05 rounded bg-su-background-message-secondary p-su-space-07 sm:w-11/12">
      <p className="!m-su-space-00 text-su-heading-200 !font-bold">{message}</p>
      {isSafeUrl(certificateUrl) && (
        <SuButton
          variant="primary"
          label={downloadLabel}
          icon={<span aria-hidden="true">&#8681;</span>}
          onClick={() => {
            // Metric only; linkProps below performs the actual download.
            clickMetric({
              feature: FeatureName.CredentialCertificateDownloaded,
              metadata: { credentialId, surface: 'completion_panel' },
            });
          }}
          linkProps={{
            href: certificateUrl,
            download: certificateUrl,
            refTag: 'su_credential_certificate_download',
          }}
        />
      )}
      {ctaLink && ctaLabel && isSafeUrl(ctaLink) && (
        <SuButton
          variant="secondary"
          label={ctaLabel}
          linkProps={{
            href: ctaLink,
            refTag: 'su_credential_completion_cta',
          }}
        />
      )}
    </div>
  );
};

export default CredentialCompletionPanel;
